import { useMutation } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { historyVariables } from '../lib/records'
import type { CostChange, CostLineInput, CostRfq } from '../types'
import { WRITE, useCostWriteQueue, type Mutate, type Reload, type Vars } from './useCostWriteQueue'

/**
 * Change and change-attachment writes (spec §6). A write reloads the change's detail and history, also after a
 * refusal; `onDetailError` hears when the detail reload itself fails (the change may be gone).
 */
export function useChangeMutations(projectId: string, onDetailError?: (changeId: string, error: unknown) => void) {
  const { saving, run } = useCostWriteQueue()
  const [create] = useMutation(Mutation.CREATE_COST_CHANGE, WRITE)
  const [update] = useMutation(Mutation.UPDATE_COST_CHANGE, WRITE)
  const [remove] = useMutation(Mutation.DELETE_COST_CHANGE, WRITE)
  const [setLines] = useMutation(Mutation.SET_COST_CHANGE_LINES, WRITE)
  const [submit] = useMutation(Mutation.SUBMIT_COST_CHANGE, WRITE)
  const [approve] = useMutation(Mutation.APPROVE_COST_CHANGE, WRITE)
  const [reject] = useMutation(Mutation.REJECT_COST_CHANGE, WRITE)
  const [reopen] = useMutation(Mutation.REOPEN_COST_CHANGE, WRITE)
  const [unapprove] = useMutation(Mutation.UNAPPROVE_COST_CHANGE, WRITE)
  const [addAttachment] = useMutation(Mutation.ADD_COST_ATTACHMENT, WRITE)
  const [removeAttachment] = useMutation(Mutation.REMOVE_COST_ATTACHMENT, WRITE)
  const [convert] = useMutation(Mutation.CONVERT_COST_POTENTIAL_CHANGE, WRITE)
  const [dismiss] = useMutation(Mutation.DISMISS_COST_POTENTIAL_CHANGE, WRITE)
  const [createRfq] = useMutation(Mutation.CREATE_COST_RFQ_FROM_POTENTIAL_CHANGE, WRITE)
  const detail = (id: string): Reload[] => [
    { query: Query.COST_CHANGE, variables: { id }, onError: onDetailError && ((e: unknown) => onDetailError(id, e)) },
    { query: Query.COST_ACTIVITY_LOG, variables: historyVariables(projectId, 'CHANGE', id) },
  ]
  const register: Reload[] = [{ query: Query.COST_CHANGES, variables: { projectId, filter: {} } }]
  const rfqs: Reload[] = [{ query: Query.COST_RFQS, variables: { projectId, filter: {} } }]
  const m = (fn: unknown) => fn as Mutate

  return {
    saving,
    create: (input: Vars) => run<{ createCostChange: CostChange }>(m(create), { projectId, input }, register),
    update: (id: string, input: Vars) => run(m(update), { id, input }, detail(id)),
    setLines: (id: string, lines: CostLineInput[]) => run(m(setLines), { id, lines }, detail(id)),
    submit: (id: string) => run(m(submit), { id }, detail(id)),
    approve: (id: string) => run(m(approve), { id }, detail(id)),
    reject: (id: string, note: string) => run(m(reject), { id, note }, detail(id)),
    reopen: (id: string) => run(m(reopen), { id }, detail(id)),
    unapprove: (id: string) => run(m(unapprove), { id }, detail(id)),
    remove: (id: string) => run(m(remove), { id }, register, detail(id)),
    addAttachment: (id: string, source: { fileId: string } | { docRevisionId: string }) => run(m(addAttachment), { target: { changeId: id }, ...source }, detail(id)),
    removeAttachment: (id: string, attachmentId: string) => run(m(removeAttachment), { id: attachmentId }, detail(id)),
    convert: (id: string, kind: 'BUDGET_CHANGE' | 'CONTRACT_CHANGE', contractId: string | null) => run(m(convert), { id, kind, contractId }, detail(id)),
    dismiss: (id: string, note: string) => run(m(dismiss), { id, note }, detail(id)),
    createRfq: (id: string) => run<{ createCostRfqFromPotentialChange: CostRfq }>(m(createRfq), { id }, [...detail(id), ...rfqs]),
  }
}

export type ChangeMutations = ReturnType<typeof useChangeMutations>
